import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ArrowLeft, Loader2, Mail, CheckCircle } from 'lucide-react'

const ForgotPasswordPage = () => {
  const { t } = useTranslation()
  const [email, setEmail] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setIsLoading(true)

    try {
      // Simulate API call
      await new Promise((resolve) => setTimeout(resolve, 1500))
      setIsSubmitted(true)
    } catch (err) {
      setError(t('auth.failedToSendReset'))
    } finally {
      setIsLoading(false)
    }
  }

  if (isSubmitted) {
    return (
      <div className="w-full max-w-lg mx-auto">
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-600 to-cyan-500 flex items-center justify-center shadow-lg shadow-blue-500/25">
              <span className="text-2xl">🇮🇳</span>
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900">{t('common.appName')}</h1>
              <p className="text-xs text-gray-500">{t('auth.nationalHealthMission')}</p>
            </div>
          </Link>
        </div>

        {/* Main Card */}
        <div className="bg-white/80 backdrop-blur-xl rounded-3xl shadow-xl border border-white/50 p-8">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-gradient-to-br from-green-400 to-emerald-500 flex items-center justify-center shadow-lg shadow-green-500/25">
              <CheckCircle className="w-8 h-8 text-white" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">{t('auth.checkYourEmail')}</h2>
            <p className="text-gray-500 mb-6">
              {t('auth.resetSentTo')}
            </p>
            <p className="text-lg font-medium text-gray-900 bg-gray-50 rounded-xl py-3 px-4 mb-6">
              {email}
            </p>
            <p className="text-sm text-gray-500 mb-6">
              {t('auth.resetSpamNote')}
            </p>
            <Link
              to="/login"
              className="inline-flex items-center justify-center gap-2 py-3 px-6 rounded-xl bg-gradient-to-r from-blue-600 to-cyan-500 text-white font-medium hover:opacity-90 transition-all shadow-lg shadow-blue-500/25"
            >
              <ArrowLeft className="w-4 h-4" />
              {t('auth.backToLogin')}
            </Link>
          </div>
        </div>

        {/* Footer */}
        <p className="text-center text-xs text-gray-400 mt-6">
          © 2026 {t('common.appName')}. {t('auth.govtInitiative')}
        </p>
      </div>
    )
  }

  return (
    <div className="w-full max-w-lg mx-auto">
      {/* Logo */}
      <div className="text-center mb-8">
        <Link to="/" className="inline-flex items-center gap-3 mb-6">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-600 to-cyan-500 flex items-center justify-center shadow-lg shadow-blue-500/25">
            <span className="text-2xl">🇮🇳</span>
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900">{t('common.appName')}</h1>
            <p className="text-xs text-gray-500">{t('auth.nationalHealthMission')}</p>
          </div>
        </Link>
      </div>

      {/* Main Card */}
      <div className="bg-white/80 backdrop-blur-xl rounded-3xl shadow-xl border border-white/50 p-8">
        {/* Header */}
        <div className="text-center mb-6">
          <h2 className="text-2xl font-bold text-gray-900">{t('auth.forgotPassword')}</h2>
          <p className="text-gray-500 mt-1">{t('auth.forgotSubtitle')}</p>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-6 p-4 rounded-xl bg-red-50 border border-red-200">
            <p className="text-red-600 text-sm text-center">{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Email */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">{t('auth.emailAddress')}</label>
            <div className="relative">
              <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full pl-11 pr-4 py-3 rounded-xl border border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none transition-all"
                placeholder={t('auth.emailPlaceholder')}
              />
            </div>
            <p className="text-xs text-gray-500 mt-2 ml-1">
              {t('auth.resetLinkNote')}
            </p>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isLoading}
            className="w-full flex items-center justify-center gap-2 py-3 px-6 rounded-xl bg-gradient-to-r from-blue-600 to-cyan-500 text-white font-medium hover:opacity-90 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-blue-500/25"
          >
            {isLoading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                {t('auth.sending')}
              </>
            ) : (
              t('auth.sendResetLink')
            )}
          </button>
        </form>

        {/* Info Box */}
        <div className="mt-6 p-4 rounded-xl bg-blue-50 border border-blue-100">
          <p className="text-sm text-blue-700 text-center">
            <strong>{t('auth.needHelp')}</strong> {t('auth.contactSupportAt')}{' '}
            <a href="mailto:support@tbmuktbharat.gov.in" className="font-medium hover:underline">
              support@tbmuktbharat.gov.in
            </a>
          </p>
        </div>

        {/* Login Link */}
        <p className="text-center text-sm text-gray-500 mt-6">
          {t('auth.rememberPassword')}{' '}
          <Link to="/login" className="text-blue-600 font-medium hover:underline">
            {t('auth.signIn')}
          </Link>
        </p>
      </div>

      {/* Footer */}
      <p className="text-center text-xs text-gray-400 mt-6">
        © 2026 {t('common.appName')}. {t('auth.govtInitiative')}
      </p>
    </div>
  )
}

export default ForgotPasswordPage